import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Button } from '@tempo-io/sui-button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@tempo-io/sui-dialog';
import { Spinner } from '@tempo-io/sui-spinner';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { createDragSensors } from '@/components/common/settings/item-types/lib/utils';
import type { IVisualizationHeaderProps } from '@/lib/api/types/roadmapping';
import { useUpdateTimelineHeaderOrderMutation } from '@/lib/mutations/roadmapping/useTimelineLayoutMutations';
import { useTimelineHeaderConfig } from '@/lib/queries/roadmapping/useTimelineHeaderConfig';
import { SortableHeaderItem } from '../swimlane/SortableHeaderItem';
import { useViewContext } from '../ViewContext';

interface ReorderHeadersModalProps {
  isOpen: boolean;
  onClose: () => void;
  headers: IVisualizationHeaderProps[];
  headerFieldId: string;
  parentFieldId: string | null;
  parentHeaderId: string | null;
  visualizationId: number;
}

export const ReorderTimelineHeadersDialog = ({
  isOpen,
  onClose,
  headerFieldId,
  headers,
  parentFieldId,
  parentHeaderId,
  visualizationId,
}: ReorderHeadersModalProps) => {
  const { viewId } = useViewContext();
  const { t } = useTranslation();
  const [localHeaders, setLocalHeaders] = useState<IVisualizationHeaderProps[]>([]);

  const { data: headerConfig } = useTimelineHeaderConfig({
    viewId,
    visualizationId,
    headerFieldId,
    parentFieldId,
    parentHeaderId,
  });
  const { mutateAsync, isPending } = useUpdateTimelineHeaderOrderMutation(viewId, visualizationId);

  const orderedHeaders = useMemo(() => {
    if (!headerConfig?.orderedHeaderIds.length) {
      return headers;
    }
    const ordered = headerConfig.orderedHeaderIds
      .map((id) => headers.find((h) => h.id?.toString() === id))
      .filter((h): h is IVisualizationHeaderProps => h !== undefined);
    const unordered = headers.filter(
      (h) => !headerConfig.orderedHeaderIds.includes(h.id?.toString() || ''),
    );
    return [...ordered, ...unordered];
  }, [headers, headerConfig]);

  useEffect(() => {
    setLocalHeaders(orderedHeaders);
  }, [orderedHeaders]);

  const sensors = createDragSensors();

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    setLocalHeaders((items) => {
      const oldIndex = items.findIndex((h) => h.id === active.id);
      const newIndex = items.findIndex((h) => h.id === over.id);

      if (oldIndex === -1 || newIndex === -1) {
        return items;
      }

      return arrayMove(items, oldIndex, newIndex);
    });
  }, []);

  const handleSave = async () => {
    const newOrder = localHeaders.map((h) => h.id);
    const hasChanges = localHeaders.some((h, i) => h.id !== orderedHeaders[i]?.id);
    if (!hasChanges) {
      onClose();
      return;
    }
    await mutateAsync({
      headerFieldId,
      headerOrder: {
        orderedHeaderIds: newOrder,
        parentFieldHeaderId: parentFieldId,
        parentHeaderId,
      },
    });
    onClose();
  };

  const handleCancel = () => {
    setLocalHeaders(orderedHeaders);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('views.visualization.reorder.headers')}</DialogTitle>
          <DialogDescription>
            {t('views.visualization.reorder.header.type.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="py-4 max-h-96 overflow-y-auto">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            autoScroll={false}
          >
            <SortableContext
              items={localHeaders.map((h) => h.id)}
              strategy={verticalListSortingStrategy}
            >
              {localHeaders.map((header, index) => (
                <SortableHeaderItem key={header.id} header={header} index={index} />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isPending}>
            {isPending && (
              <span className="mr-2">
                <Spinner size={16} />
              </span>
            )}
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
